// Operator › Connections › Memory review window (RECALL-INBOX-LOOP): how many days a card in the
// Inbox's Memory lane waits with its department before it also goes to the owner/CEO. The platform
// default (3 days) and a per-company override are the `memory_lane` provisioning setting
// (`GET`/`PUT /v1/operator/provisioning/settings`), stored data a platform admin edits here in
// modals; a company without an override follows the default.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../ops-parts";

type Setting = OperationOutput<"operator.provisioning.settings.get">["settings"][number];
type Days = { escalation_days: number };

const daysOf = (v: unknown): number | null => {
  const d = (v as Partial<Days> | null)?.escalation_days;
  return typeof d === "number" ? d : null;
};

const laneKey = (companyId: string | null) =>
  ["operator", "provisioning", "memory_lane", companyId ?? "platform"] as const;
const laneQuery = (api: Api, companyId: string | null) =>
  queryOptions({
    queryKey: laneKey(companyId),
    queryFn: async (): Promise<Setting | null> => {
      const r = await api.client.operator.provisioning.settings.get({
        query: companyId ? { company_id: companyId } : {},
      });
      return r.settings.find((s) => s.key === "memory_lane") ?? null;
    },
  });

export function MemoryLaneCard({ api }: { api: Api }) {
  const q = useQuery(laneQuery(api, null));
  const [editing, setEditing] = useState(false);
  const [override, setOverride] = useState(false);
  const days = daysOf(q.data?.platform) ?? daysOf(q.data?.default);
  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-slot="memory-lane-setting"
      aria-label="Memory review window"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          Memory review window
          <InfoTip label="About the memory review window">
            Days a memory card waits with its department's reviewer before it also goes up to the
            owner. A company can have its own number.
          </InfoTip>
        </h2>
        {q.data ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            <Button variant="secondary" size="xs" onClick={() => setEditing(true)}>
              <PencilIcon aria-hidden /> Edit
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setOverride(true)}>
              Company override
            </Button>
          </div>
        ) : null}
      </div>
      {q.isPending ? (
        <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Notice tone="danger" className="mt-3">
          {problemText(q.error, "The memory review window could not be loaded.")}
        </Notice>
      ) : (
        <p className="mt-3 text-xs" data-slot="memory-lane-days">
          <span className="text-muted-foreground">Default </span>
          {days} {days === 1 ? "day" : "days"}
        </p>
      )}
      {editing && q.data ? (
        <DaysDialog
          api={api}
          title="Memory review window"
          info="Every company without its own number uses this."
          companyId={null}
          start={days ?? 3}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {override ? <OverrideDialog api={api} onClose={() => setOverride(false)} /> : null}
    </section>
  );
}

function DaysDialog({
  api,
  title,
  info,
  companyId,
  start,
  onClose,
}: {
  api: Api;
  title: string;
  info: string;
  companyId: string | null;
  start: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const id = useId();
  const [value, setValue] = useState(String(start));
  const n = Number(value);
  const valid = Number.isInteger(n) && n >= 1 && n <= 30;
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.provisioning.settings.put({
        params: { key: "memory_lane" },
        body: { company_id: companyId, value: { escalation_days: n } },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "provisioning", "memory_lane"] });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={title}
      info={info}
      submitLabel="Save"
      pending={save.isPending}
      canSubmit={valid}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={
        save.error ? (
          <p className="text-sm text-danger">{problemText(save.error, "Not saved.")}</p>
        ) : null
      }
      slot="memory-lane-dialog"
    >
      <Field>
        <FieldLabel htmlFor={`${id}-days`}>Days (1 to 30)</FieldLabel>
        <Input
          id={`${id}-days`}
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value.trim())}
        />
      </Field>
    </QuickAddDialog>
  );
}

/** One company's own number: pick the company, set it or clear it (back to the default). */
function OverrideDialog({ api, onClose }: { api: Api; onClose: () => void }) {
  const qc = useQueryClient();
  const id = useId();
  const companies = useQuery({
    queryKey: ["operator", "companies", "names"],
    queryFn: () => api.client.operator.companies.list({}),
  });
  const [companyId, setCompanyId] = useState("");
  const current = useQuery({
    ...laneQuery(api, companyId || null),
    enabled: companyId !== "",
  });
  const own = daysOf(current.data?.company);
  const [value, setValue] = useState("");
  const shown = value || String(own ?? daysOf(current.data?.effective) ?? 3);
  const n = Number(shown);
  const valid = companyId !== "" && Number.isInteger(n) && n >= 1 && n <= 30;
  const put = useMutation({
    mutationFn: (v: Days | null) =>
      api.client.operator.provisioning.settings.put({
        params: { key: "memory_lane" },
        body: { company_id: companyId, value: v },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "provisioning", "memory_lane"] });
      setValue("");
    },
  });
  return (
    <QuickAddDialog
      title="Company override"
      info="A company with its own number ignores the default. Clearing it goes back to the default."
      submitLabel="Save"
      pending={put.isPending}
      canSubmit={valid}
      onSubmit={() => put.mutate({ escalation_days: n })}
      onClose={onClose}
      error={
        put.error ? (
          <p className="text-sm text-danger">{problemText(put.error, "Not saved.")}</p>
        ) : null
      }
      slot="memory-lane-override-dialog"
    >
      <Field>
        <FieldLabel htmlFor={`${id}-co`}>Company</FieldLabel>
        <NativeSelect
          id={`${id}-co`}
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            setValue("");
          }}
          className="w-full"
        >
          <NativeSelectOption value="">Choose a company</NativeSelectOption>
          {(companies.data?.data ?? []).map((c) => (
            <NativeSelectOption key={c.company.id} value={c.company.id}>
              {c.company.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      {companyId ? (
        <>
          <Field>
            <FieldLabel htmlFor={`${id}-days`}>Days (1 to 30)</FieldLabel>
            <Input
              id={`${id}-days`}
              inputMode="numeric"
              value={shown}
              onChange={(e) => setValue(e.target.value.trim())}
            />
          </Field>
          <p className="text-xs text-muted-foreground" data-slot="memory-lane-company-state">
            {own !== null ? `Its own number: ${own} days.` : "Follows the default."}
          </p>
          {own !== null ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="justify-self-start"
              disabled={put.isPending}
              onClick={() => put.mutate(null)}
            >
              <Trash2Icon aria-hidden /> Clear override
            </Button>
          ) : null}
        </>
      ) : null}
    </QuickAddDialog>
  );
}
